
export type ToastType = 'success' | 'error' | 'warning';

const TOAST_CONTAINER_ID = 'cop-toast-container';
const TOAST_AUTO_DISMISS_MS = 5000;

const TOAST_CONFIG: Record<ToastType, { label: string; iconSvg: string }> = {
  success: {
    label: 'Success',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" stroke-width="2"/>
      <path d="M7 12.5l3.5 3.5 6.5-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  error: {
    label: 'Error',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" stroke-width="2"/>
      <line x1="12" y1="7" x2="12" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <circle cx="12" cy="16.5" r="1.25" fill="currentColor"/>
    </svg>`,
  },
  warning: {
    label: 'Warning',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" stroke-width="2"/>
      <line x1="12" y1="7" x2="12" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <circle cx="12" cy="16.5" r="1.25" fill="currentColor"/>
    </svg>`,
  },
};

function getOrCreateContainer(): HTMLElement {
  let container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'false');
    container.classList.add('cop-toast-container');
    document.body.appendChild(container);
  }
  return container;
}

function show(type: ToastType, message: string, customLabel?: string): void {
  if (!message || typeof message !== 'string') return;

  const { label: defaultLabel, iconSvg } = TOAST_CONFIG[type];
  const label = customLabel?.trim() || defaultLabel;
  const container = getOrCreateContainer();

  const toast = document.createElement('div');
  toast.classList.add('cop-toast', `cop-toast--${type}`);
  toast.setAttribute('role', 'alert');

  toast.innerHTML = `
    <div class="cop-toast__icon" aria-hidden="true">${iconSvg}</div>
    <div class="cop-toast__body">
      <p class="cop-toast__title">${label}</p>
      <p class="cop-toast__message">${message}</p>
    </div>
    <button class="cop-toast__close" aria-label="Close notification" type="button">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  `;

  const closeButton = toast.querySelector<HTMLButtonElement>('.cop-toast__close');
  closeButton?.addEventListener('click', () => dismiss(toast));

  container.appendChild(toast);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('cop-toast--visible'));
  });

  const timerId = window.setTimeout(() => dismiss(toast), TOAST_AUTO_DISMISS_MS);
  toast.dataset.timerId = String(timerId);
}

function dismiss(toast: HTMLElement): void {
  const timerId = toast.dataset.timerId;
  if (timerId) clearTimeout(Number(timerId));

  toast.classList.remove('cop-toast--visible');
  toast.classList.add('cop-toast--hiding');

  toast.addEventListener(
    'transitionend',
    () => {
      toast.remove();
    },
    { once: true }
  );
}

export const ToastService = {

  success(message: string, label?: string): void {
    show('success', message, label);
  },

  error(message: string, label?: string): void {
    show('error', message, label);
  },

  warning(message: string, label?: string): void {
    show('warning', message, label);
  },
};